import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getWalletClient, waitForTransactionReceipt } from "wagmi/actions";
import { erc20Abi, parseUnits, type Address } from "viem";
import { keelVaultAbi } from "../abi";
import { esIgual, type RedKeel } from "../config/networks";
import type { EstadoVault } from "./useVault";
import { clientePublico } from "../lib/cliente";

/** Solo el `slot0`, para poner el minimo de salida del swap de cierre contra el precio de AHORA. */
const POOL_SLOT0_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Slipstream devuelve 6 campos, Uniswap 7; con 2 vale para ambos
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
  // liquidez ACTIVA de la pool: para estimar el impacto de precio de convertir a estable al salir.
  { type: "function", name: "liquidity", stateMutability: "view", inputs: [], outputs: [{ type: "uint128" }] },
] as const;

// SALIR CONVIRTIENDO A ESTABLE: si convertir todo el activo a estable moviera el precio más que esto
// (impacto de precio en bps), NO se convierte — se saca en las dos monedas para no perder por slippage.
// Es el precio-move (≈ 2× la pérdida media); 200 bps = 2% de impacto ≈ ~1% de pérdida = "muy alto".
const IMPACTO_MAX_SALIDA_BPS = 200;

/** SwapRouter original: lleva `deadline` dentro del struct. */
const ROUTER_ABI = [
  { type: "function", name: "exactInputSingle", stateMutability: "payable",
    inputs: [{ type: "tuple", components: [
      { name: "tokenIn", type: "address" }, { name: "tokenOut", type: "address" },
      { name: "fee", type: "uint24" }, { name: "recipient", type: "address" },
      { name: "deadline", type: "uint256" }, { name: "amountIn", type: "uint256" },
      { name: "amountOutMinimum", type: "uint256" }, { name: "sqrtPriceLimitX96", type: "uint160" },
    ] }],
    outputs: [{ type: "uint256" }] },
] as const;

/** SwapRouter02: el mismo struct SIN `deadline`. Cuál toca lo dice el propio vault. */
const ROUTER02_ABI = [
  { type: "function", name: "exactInputSingle", stateMutability: "payable",
    inputs: [{ type: "tuple", components: [
      { name: "tokenIn", type: "address" }, { name: "tokenOut", type: "address" },
      { name: "fee", type: "uint24" }, { name: "recipient", type: "address" },
      { name: "amountIn", type: "uint256" }, { name: "amountOutMinimum", type: "uint256" },
      { name: "sqrtPriceLimitX96", type: "uint160" },
    ] }],
    outputs: [{ type: "uint256" }] },
] as const;

const Q192 = 1n << 192n;

/** Avance de una escritura. Cuando hay dos firmas, `aprobando` (dar permiso al token) va antes de
 *  `firmando` (la operación en sí). Luego `enviada` (con hash) y `ok`, o `error` con su mensaje. */
export type Progreso = {
  nombre: string;
  fase: "aprobando" | "firmando" | "enviada" | "ok" | "error";
  /** El flujo tiene dos firmas (aprobar + operar): sirve para el «Firma 1 de 2 / 2 de 2». */
  dos?: boolean;
  hash?: `0x${string}`;
  mensaje?: string;
  /** Aviso extra en la pantalla de ÉXITO (p. ej. "no convertí a estable por slippage alto"): la
   *  operación salió bien, pero hay algo que el usuario debe saber. */
  nota?: string;
};

/**
 * TODAS las escrituras sobre un vault, en un solo sitio.
 *
 * Cada una devuelve una promesa y refresca el estado al terminar. Lo que NO hacen es decidir si se
 * pueden hacer: eso lo sabe el contrato, y la pantalla lo pregunta simulando (`puedeLlamar`), que
 * es la unica forma de saber que entiende un clon EIP-1167.
 */
export function useAcciones(v: EstadoVault | undefined, red: RedKeel | undefined) {
  const { address } = useAccount();
  const config = useConfig();
  const qc = useQueryClient();
  const [haciendo, setHaciendo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  // Avance de la operación en curso, para el modal de proceso (logo de Keel cargando).
  const [progreso, setProgreso] = useState<Progreso | null>(null);
  const nombreRef = useRef<string>("");
  // Aviso a mostrar en el ÉXITO de la operación en curso (lo fija la propia acción, p. ej. sacarTodo
  // cuando decide NO convertir a estable por slippage alto). Se limpia al empezar cada operación.
  const notaRef = useRef<string>("");
  // Cambia el paso dentro de una operación (p. ej. de aprobar a firmar) sin perder de qué acción es.
  const paso = (fase: Progreso["fase"], dos = false) => setProgreso({ nombre: nombreRef.current, fase, dos });

  // El dueño se calcula con la wallet CONECTADA AHORA (no con el `esMio` cacheado del vault), para
  // que cambiar de cuenta lo refleje al instante. Si no eres el dueño, la piscina es SOLO LECTURA.
  const esMio = esIgual(v?.dueno, address);

  async function ejecutar(nombre: string, fn: () => Promise<`0x${string}`>) {
    if (!v || !red) return;
    // Candado de seguridad: NINGUNA acción se firma si la wallet conectada no es la dueña. Es
    // redundante con el `onlyOwner` del contrato (que revertiría), pero corta ANTES de pedir firma
    // —el usuario no debería ni poder intentarlo—. Todas las acciones pasan por aquí.
    if (!esMio) {
      setError("Solo el dueño de esta piscina puede operar. Con otra wallet es solo lectura.");
      return;
    }
    setError(null);
    setHecho(null);
    setHaciendo(nombre);
    nombreRef.current = nombre;
    notaRef.current = "";
    setProgreso({ nombre, fase: "firmando" });
    try {
      const hash = await fn();
      // Ya firmado y enviado: ahora se espera a que la cadena lo confirme.
      setProgreso({ nombre, fase: "enviada", hash });
      await waitForTransactionReceipt(config, { hash, chainId: red.idCadena });
      await qc.invalidateQueries({ queryKey: ["vault"] });
      await qc.invalidateQueries({ queryKey: ["vaults"] });
      setHecho(nombre);
      setProgreso({ nombre, fase: "ok", hash, nota: notaRef.current || undefined });
    } catch (e) {
      const m = mensaje(e);
      setError(m);
      setProgreso({ nombre, fase: "error", mensaje: m });
    } finally {
      setHaciendo(null);
    }
  }

  const wallet = () => getWalletClient(config, { chainId: red!.idCadena });
  const escribir = async (functionName: string, args: readonly unknown[] = []) => {
    const w = await wallet();
    return w.writeContract({
      address: v!.direccion,
      abi: keelVaultAbi,
      functionName,
      args,
    } as never);
  };

  /** Aprueba si hace falta y deposita. Se usa para añadir fondos, gas y reserva. */
  async function aprobarY(token: Address, cantidad: bigint, luego: () => Promise<`0x${string}`>) {
    const publico = clientePublico(red!.idCadena);
    const w = await wallet();
    const permitido = await publico.readContract({
      address: token, abi: erc20Abi, functionName: "allowance", args: [address!, v!.direccion],
    });
    if (permitido < cantidad) {
      paso("aprobando", true);
      const h = await w.writeContract({
        address: token, abi: erc20Abi, functionName: "approve", args: [v!.direccion, cantidad],
      });
      await waitForTransactionReceipt(config, { hash: h, chainId: red!.idCadena });
      paso("firmando", true);
    }
    return luego();
  }

  const tokenRef = () => (v!.refEsToken0 ? v!.token0 : v!.token1);
  /** El que NO es la referencia: la acción del par, lo que hay que convertir para salir en estable. */
  const tokenActivo = () => (v!.refEsToken0 ? v!.token1 : v!.token0);

  /**
   * Vende `cantidad` del activo por el estable, desde la WALLET del usuario y con su router.
   *
   * El router y el `fee` se leen DEL VAULT, no de una tabla nuestra: es el mismo cableado con el
   * que el vault lleva operando toda su vida, así que no puede apuntar a otra red ni a otra pool.
   *
   * El mínimo de salida sale del `slot0` de ese instante, descontando la comisión de la pool y el
   * mismo deslizamiento que el dueño le tiene puesto al vault. Sin descontar la comisión, en una
   * pool del 0,3% el margen se lo come la propia pool y el swap revierte sin motivo aparente.
   */
  async function venderActivoPorEstable(cantidad: bigint) {
    const publico = clientePublico(red!.idCadena);
    const w = await wallet();
    const activo = tokenActivo();
    const estable = tokenRef();
    const activoEsToken0 = !v!.refEsToken0;

    const [router, conDeadline, fee, slot0] = await publico.multicall({
      allowFailure: false,
      contracts: [
        { address: v!.direccion, abi: keelVaultAbi, functionName: "router" },
        { address: v!.direccion, abi: keelVaultAbi, functionName: "routerHasDeadline" },
        { address: v!.direccion, abi: keelVaultAbi, functionName: "fee" },
        { address: v!.pool, abi: POOL_SLOT0_ABI, functionName: "slot0" },
      ],
    });

    const sqrtP = BigInt(slot0[0]);
    const neto = (cantidad * (1_000_000n - BigInt(fee))) / 1_000_000n; // lo que queda tras la pool
    const bruto = activoEsToken0
      ? (neto * sqrtP * sqrtP) / Q192  // vendo token0, recibo token1
      : (neto * Q192) / (sqrtP * sqrtP); // vendo token1, recibo token0
    const slip = BigInt(v!.swapSlippageBps || 50);
    const mínimo = (bruto * (10_000n - slip)) / 10_000n;

    const permitido = await publico.readContract({
      address: activo.address, abi: erc20Abi, functionName: "allowance",
      args: [address!, router as Address],
    });
    if (permitido < cantidad) {
      paso("aprobando", true);
      const h = await w.writeContract({
        address: activo.address, abi: erc20Abi, functionName: "approve",
        args: [router as Address, cantidad],
      });
      await waitForTransactionReceipt(config, { hash: h, chainId: red!.idCadena });
      paso("firmando", true);
    }

    const comun = {
      tokenIn: activo.address, tokenOut: estable.address, fee,
      recipient: address!, amountIn: cantidad, amountOutMinimum: mínimo,
      sqrtPriceLimitX96: 0n,
    };
    return conDeadline
      ? w.writeContract({
          address: router as Address, abi: ROUTER_ABI, functionName: "exactInputSingle",
          args: [{ ...comun, deadline: BigInt(Math.floor(Date.now() / 1000) + 600) }],
        })
      : w.writeContract({
          address: router as Address, abi: ROUTER02_ABI, functionName: "exactInputSingle",
          args: [comun],
        });
  }

  /**
   * FIRMA Y CONVIERTE. Ejecuta la salida de dinero (`firmar`) y, si `aEstable`, vende después el
   * activo que haya ENTRADO en la wallet con esa firma. Sirve igual para el cierre entero que para
   * la ganancia suelta: en los dos casos sale dinero del vault en los DOS tokens del par.
   *
   * Dos decisiones que no son de estilo:
   *
   *  · EL SWAP VA DESPUÉS, NUNCA DENTRO. La salida es lo único garantizado que tiene el dueño; si
   *    el swap viviera dentro del contrato, un precio movido, una pool sin liquidez o un
   *    deslizamiento pasado harían REVERTIR la salida y el dinero se quedaría encerrado por querer
   *    convertirlo. Así, si el swap falla, el dueño ya tiene el dinero y como mucho se queda sin
   *    convertir.
   *
   *  · SE CONVIERTE EL DELTA, NO EL SALDO. Se mide el activo en la wallet antes y después y se
   *    vende solo la diferencia. Quien ya tuviera NVDA suyo de antes no quiere que este botón se lo
   *    venda de paso.
   *
   * @param yaEsTuyo con qué empieza el error si falla la venta. El dinero YA salió, y un «revert»
   *                 pelado hace pensar que se ha perdido algo.
   */
  async function firmarYConvertir(
    aEstable: boolean,
    firmar: () => Promise<`0x${string}`>,
    yaEsTuyo: string
  ) {
    const publico = clientePublico(red!.idCadena);
    const activo = tokenActivo();
    const saldo = () => publico.readContract({
      address: activo.address, abi: erc20Abi, functionName: "balanceOf", args: [address!],
    });

    const antes = aEstable ? await saldo() : 0n;
    const hash = await firmar();
    if (!aEstable) return hash;

    await waitForTransactionReceipt(config, { hash, chainId: red!.idCadena });
    const despues = await saldo();
    const entro = despues > antes ? despues - antes : 0n;
    // Pudo salir ya todo en el estable —una posición fuera de rango por el lado bueno, o una
    // ganancia que solo tenía el token de referencia—: entonces no hay nada que vender y la segunda
    // firma se ahorra.
    if (entro === 0n) return hash;

    try {
      return await venderActivoPorEstable(entro);
    } catch (e) {
      await qc.invalidateQueries({ queryKey: ["vault"] });
      await qc.invalidateQueries({ queryKey: ["vaults"] });
      throw new Error(
        `${yaEsTuyo} Lo que no salió fue la venta de ${activo.symbol} a ${tokenRef().symbol}: ` +
          `${mensaje(e)} Puedes venderlo cuando quieras, sin prisa — el dinero ya es tuyo.`
      );
    }
  }

  /**
   * Estima el IMPACTO DE PRECIO (en bps) de convertir TODO el activo del vault a estable al salir.
   * Usa la liquidez ACTIVA de la pool y el sqrt-precio (misma cuenta que el tope del keeper). Sirve
   * para decidir si conviene convertir o es mejor sacar en las dos monedas. `null` si no se puede
   * leer (entonces NO se bloquea: se intenta convertir como siempre).
   */
  async function impactoConversionBps(): Promise<number | null> {
    try {
      if (!v) return null;
      const activoEsToken0 = !v.refEsToken0;
      const activo = tokenActivo();
      const humano =
        (activoEsToken0 ? v.enPosicion.a0 : v.enPosicion.a1) +
        (activoEsToken0 ? v.libre.a0 : v.libre.a1); // activo que saldría (posición + libre)
      if (humano <= 0) return 0; // no hay activo que convertir -> sin slippage
      const publico = clientePublico(red!.idCadena);
      const [liq, slot0] = await publico.multicall({
        allowFailure: false,
        contracts: [
          { address: v.pool, abi: POOL_SLOT0_ABI, functionName: "liquidity" },
          { address: v.pool, abi: POOL_SLOT0_ABI, functionName: "slot0" },
        ],
      });
      const L = Number(liq);
      const sp = Number(slot0[0]) / 2 ** 96;
      if (!L || sp <= 0) return null;
      const amount = humano * 10 ** activo.decimals; // crudo
      let impacto: number;
      if (activoEsToken0) {
        const spNew = 1 / (1 / sp + amount / L); // vendo token0 -> precio baja
        impacto = 1 - (spNew / sp) ** 2;
      } else {
        const spNew = sp + amount / L; // vendo token1 -> precio sube
        impacto = (spNew / sp) ** 2 - 1;
      }
      return Math.abs(impacto) * 10_000;
    } catch {
      return null;
    }
  }

  return {
    haciendo, error, hecho, progreso,
    /** true cuando la wallet conectada NO es la dueña → la UI muestra la piscina en solo lectura y
     *  desactiva todos los botones de acción. El candado de verdad está en `ejecutar`. */
    soloLectura: !esMio,
    limpiar: () => { setError(null); setHecho(null); },
    cerrarProceso: () => setProgreso(null),

    // ---------------------------------------------------------------- dinero que entra
    /** Añade capital a la posicion que ya existe, en vez de partirlo en dos vaults. */
    anadirFondos: (cantidad: string, esToken0: boolean) =>
      ejecutar("anadirFondos", async () => {
        const tok = esToken0 ? v!.token0 : v!.token1;
        const wei = parseUnits(cantidad, tok.decimals);
        return aprobarY(tok.address, wei, async () =>
          escribir("deposit", [tok.address, wei])
        );
      }),

    /** Bolsa de gas. Va en el token que se deposita, y es ADICIONAL al capital. */
    cargarGas: (cantidad: string, esToken0: boolean) =>
      ejecutar("cargarGas", async () => {
        const tok = esToken0 ? v!.token0 : v!.token1;
        const wei = parseUnits(cantidad, tok.decimals);
        return aprobarY(tok.address, wei, async () =>
          escribir("addGasBudget", [esToken0 ? wei : 0n, esToken0 ? 0n : wei])
        );
      }),

    /** La reserva DCA vive SIEMPRE en el token de referencia (el estable del par). */
    anadirReserva: (cantidad: string) =>
      ejecutar("anadirReserva", async () => {
        const tok = tokenRef();
        const wei = parseUnits(cantidad, tok.decimals);
        const dir = v!.refEsToken0 ? v!.token0.address : v!.token1.address;
        return aprobarY(dir, wei, async () => escribir("addReserve", [wei]));
      }),

    recargarCuota: (n: number) => ejecutar("recargarCuota", () => escribir("addQuota", [n])),

    // ---------------------------------------------------------------- dinero que sale
    /** Devuelve el gas SIN GASTAR sin cerrar la posicion. Antes la unica salida era withdraw(),
     *  que cierra la inversion entera para recuperar una bolsa sobredimensionada. */
    devolverGas: (a0: bigint, a1: bigint) =>
      ejecutar("devolverGas", () => escribir("withdrawGasBudget", [a0, a1])),

    /**
     * Cobra al NFT y paga en la misma transaccion: entrega lo apartado MAS lo que Uniswap todavia
     * retiene, descontada la comision de Keellabs.
     *
     * Con `aEstable`, ademas vende lo que llegue en el activo y la ganancia queda ENTERA en la
     * moneda de referencia. No es un adorno: las comisiones de la pool se cobran en los dos tokens,
     * asi que retirar sin convertir deja un pico de NVDA que el dueño no pidio y que tiene que
     * vender a mano. Es la misma opcion que ya tenia el cierre, y con el mismo trato (el swap va
     * despues de la firma y solo sobre el delta — ver `firmarYConvertir`).
     */
    retirarGanancias: (aEstable = false) =>
      ejecutar("retirarGanancias", () =>
        firmarYConvertir(
          aEstable,
          () => escribir("withdrawFees"),
          "La ganancia se cobró bien y ya la tienes en tu wallet."
        )
      ),

    reinvertirGanancias: () => ejecutar("reinvertirGanancias", () => escribir("reinvestProfit")),

    /** Vuelve al rango ESTRECHO al precio de ahora sin vender el activo: se aporta `cantidad` del
     *  token de referencia (el estable) para equilibrar. Solo en vaults con la lógica nueva. */
    recenterTight: (cantidad: string) =>
      ejecutar("recenterTight", async () => {
        const tok = tokenRef();
        const wei = parseUnits(cantidad || "0", tok.decimals);
        const add0 = v!.refEsToken0 ? wei : 0n;
        const add1 = v!.refEsToken0 ? 0n : wei;
        if (wei > 0n) {
          return aprobarY(tok.address, wei, async () => escribir("recenterTight", [add0, add1]));
        }
        return escribir("recenterTight", [add0, add1]);
      }),

    /** ACHICAR el rango aportando estable, sin vender el activo: estrecha a la banda
     *  `[centro-below, centro+above]` aportando `addStableWei` del token de referencia. Sube el
     *  objetivo de recuperación a "inicial + aportado". `below/above` y el estable exacto los calcula
     *  el KeelAchicarQuoter. */
    achicarRango: (below: number, above: number, addStableWei: bigint) =>
      ejecutar("achicarRango", async () => {
        const tok = tokenRef();
        if (addStableWei > 0n) {
          return aprobarY(tok.address, addStableWei, async () =>
            escribir("achicarRango", [below, above, addStableWei]));
        }
        return escribir("achicarRango", [below, above, addStableWei]);
      }),

    /** Saca una fraccion de la posicion. El objetivo de recuperacion baja EN PROPORCION. */
    quitarLiquidez: (pct: number) =>
      ejecutar("quitarLiquidez", () => escribir("removeLiquidity", [Math.round(pct * 100)])),

    /**
     * Cierra TODO y devuelve capital, ganancias y gas sin gastar. Es irreversible.
     *
     * Con `aEstable`, además vende el activo que salga y deja el cierre entero en la moneda de
     * referencia. El cómo —swap después del cierre y solo sobre el delta— vive en
     * `firmarYConvertir`, que es lo mismo que hace «Cobrar ganancias».
     */
    sacarTodo: (aEstable = false) =>
      ejecutar("sacarTodo", async () => {
        // GUARDA DE SLIPPAGE AL SALIR: si convertir TODO el activo a estable moviera el precio más de
        // IMPACTO_MAX_SALIDA_BPS (pool fina), NO se convierte —se saca en las DOS monedas para no
        // perderlo en slippage— y se avisa en el éxito. El dinero sale igual; solo se omite el swap caro.
        let convertir = aEstable;
        if (aEstable) {
          const bps = await impactoConversionBps();
          if (bps != null && bps > IMPACTO_MAX_SALIDA_BPS) {
            convertir = false;
            const activo = tokenActivo(), estable = tokenRef();
            notaRef.current =
              `No convertí a ${estable.symbol}: hacerlo ahora costaría ~${(bps / 100).toFixed(1)}% de ` +
              `impacto de precio (la pool está fina). Sacaste TODO tu dinero en las dos monedas ` +
              `(${activo.symbol} + ${estable.symbol}) a tu wallet. Puedes convertir cuando la pool ` +
              `tenga más liquidez o en un exchange.`;
          }
        }
        // ¿SABE ESTE VAULT SALIR CONVERTIDO ÉL SOLO? `withdrawTo` vive en la lógica nueva: vende
        // dentro de la misma transacción y cobra ahí la comisión de conversión. Los vaults que
        // nacieron antes no la entienden, y a esos hay que cerrarlos y vender desde la wallet.
        //
        // Se PREGUNTA simulando, no se deduce de una lista de implementaciones: un clon EIP-1167 no
        // sabe decir qué firmas conoce, y una lista se queda vieja en el primer `setLogic`.
        if (convertir) {
          const sabeSolo = await clientePublico(red!.idCadena)
            .simulateContract({
              address: v!.direccion, abi: keelVaultAbi, functionName: "withdrawTo",
              args: [true], account: address!,
            })
            .then(() => true)
            .catch(() => false);
          if (sabeSolo) return escribir("withdrawTo", [true]);
        }
        return firmarYConvertir(
          convertir,
          () => escribir("withdraw"),
          "El vault se cerró bien y tienes TODO en tu wallet."
        );
      }),

    // ---------------------------------------------------------------- ajustes
    guardarRango: (semiancho: number) => ejecutar("guardarRango", () => escribir("setRange", [semiancho])),
    // Rango asimétrico: dos semianchos por lado. El próximo re-centrado adopta la forma y la conserva.
    guardarRangoAsym: (below: number, above: number) =>
      ejecutar("guardarRangoAsym", () => escribir("setRangeAsym", [below, above])),

    guardarGuardias: (twap: number, desviacion: number, slippageBps: number, cooldown: number) =>
      ejecutar("guardarGuardias", () =>
        escribir("setGuards", [twap, desviacion, slippageBps, cooldown])
      ),

    guardarRecuperacion: (on: boolean, maxTicks: number, ventaMin: boolean, maxBps: number) =>
      ejecutar("guardarRecuperacion", () =>
        escribir("setRecovery", [on, maxTicks, ventaMin, maxBps])
      ),

    /** Colchón del rango de recuperación: cuánto POR ENCIMA del capital apunta. 0 = al capital
     *  pelado, que es como se comportaron siempre los vaults anteriores. */
    guardarColchon: (bps: number) =>
      ejecutar("guardarColchon", () => escribir("setRecoverMargin", [bps])),

    /** Colchón FIJO, en unidades crudas del token de referencia. Sustituye al porcentual. */
    guardarColchonFijo: (importe: bigint) =>
      ejecutar("guardarColchon", () => escribir("setRecoverMarginRef", [importe])),

    guardarReinyeccion: (modo: number, pctBps: number, caidaBps: number) =>
      ejecutar("guardarReinyeccion", () =>
        escribir("setReinjection", [modo, pctBps, caidaBps])
      ),

    cambiarReinversion: (on: boolean) => ejecutar("cambiarReinversion", () => escribir("setReinvest", [on])),

    pausar: (on: boolean) => ejecutar("pausar", () => escribir("setPaused", [on])),

    cambiarOperador: (dir: Address) => ejecutar("cambiarOperador", () => escribir("setOperator", [dir])),

    /** Detiene el robot del todo. Es `setOperator(0)`: el vault sigue siendo tuyo y el dinero
     *  tambien, pero nadie lo reajusta hasta que vuelvas a poner un operador. */
    detenerRobot: () =>
      ejecutar("detenerRobot", () =>
        escribir("setOperator", ["0x0000000000000000000000000000000000000000"])
      ),

    /** Adopta la logica que publica la factory. Solo el dueño, y solo a esa direccion. */
    actualizarLogica: () => ejecutar("actualizarLogica", () => escribir("upgradeRebalancer")),
  };
}

/** Los reverts de un clon llegan pelados. Se traduce lo que se pueda a algo accionable. */
function mensaje(e: unknown): string {
  const t = e instanceof Error ? e.message : String(e);
  if (/User rejected|denied transaction/i.test(t)) return "Cancelaste la firma en tu wallet.";
  if (/NotOwner/.test(t)) return "Solo el dueño del vault puede hacer eso.";
  if (/NoPosition/.test(t)) return "Este vault no tiene ninguna posición abierta.";
  if (/Insufficient/.test(t)) return "No hay saldo suficiente para eso.";
  if (/BadParam/.test(t)) return "Alguno de los valores no es válido para el contrato.";
  if (/Cooldown/.test(t)) return "Todavía no ha pasado el tiempo mínimo entre reajustes.";
  if (/NoQuota/.test(t)) return "Se acabó la cuota de reajustes. Recárgala en Ajustes.";
  if (/BadPrice/.test(t)) return "El precio spot y el TWAP están demasiado separados ahora mismo. Vuelve a intentarlo en unos minutos.";
  if (/AlreadyOpen/.test(t)) return "El vault ya tiene una posición abierta.";
  return t.split("\n")[0] ?? t;
}

export type Acc = ReturnType<typeof useAcciones>;
